import React, {createContext, useContext, useReducer, ReactNode} from "react";
import {PromptVariable, VariableTypes} from "@/types/node";
import {VariableParams} from "@/types/general";
import {ProcessedNode} from "@/types/node";
import {CustomAction} from "@/api/services/customAction";
import {LLMVariable} from "@/api/services/lLMVariable";
import {VariableType} from "@/components/settings/constants";

export interface Variable {
  id: string;
  name: string;
  description?: string;
  type: VariableType;
  value?: string;
  isValid?: boolean;
  source?: string;
  instructions?: string;
  required?: boolean;
  enumOptions?: string[];
  subflowId?: string;
  subflowName?: string;
}
interface VariablesState {
  variables: Variable[];
}

type VariablesAction =
  | {type: "SET_VARIABLES"; payload: Variable[]}
  | {
      type: "SET_SUBFLOW_VARIABLES";
      payload: {subflowId: string; variables: Variable[]};
    }
  | {type: "ADD_VARIABLE"; payload: Variable}
  | {
      type: "UPDATE_VARIABLE";
      payload: {name: string; updates: Partial<Variable>};
    }
  | {type: "REMOVE_VARIABLE"; payload: string}
  | {type: "CLEAR_VARIABLES"};

interface VariablesContextType {
  variables: Variable[];
  getVariablesByType: (type: Variable["type"]) => Variable[];
  getAllVariables: () => Variable[];
  getSelectedSubflowVariables: (subflowId?: string) => Variable[];
  addVariable: (variable: Variable) => void;
  updateVariable: (name: string, updates: Partial<Variable>) => void;
  removeVariable: (name: string) => void;
  clearVariables: () => void;
  syncVariablesWithNodes: (
    nodes: ProcessedNode[],
    subflowId: string,
    subflowName: string,
  ) => Variable[];
}

const VariablesContext = createContext<VariablesContextType | undefined>(
  undefined,
);

export const getVariableParams = (): VariableParams => {
  const variableParams = {
    [VariableTypes.VARIABLES]: {
      label: "Variables",
      name: "Variables",
      icon: "{}",
      open: "{",
      close: "}",
      content: [] as PromptVariable[],
    },
    // [VariableTypes.ACTIONS]: {
    //   label: "Actions",
    //   name: "Custom Actions",
    //   icon: "#",
    //   open: "#",
    //   close: " ",
    //   content: [] as PromptVariable[],
    // },
    [VariableTypes.RESULTS]: {
      label: "Results",
      name: "Action Results",
      icon: "<>",
      open: "<",
      close: ">",
      content: [] as PromptVariable[],
    },
  };

  return variableParams;
};

const variablesReducer = (
  state: VariablesState,
  action: VariablesAction,
): VariablesState => {
  let newState: VariablesState;

  switch (action.type) {
    case "SET_VARIABLES":
      newState =
        action.payload.length > 0 ? {variables: action.payload} : state;
      break;
    case "SET_SUBFLOW_VARIABLES": {
      // Remove variables from this subflow and add the new ones
      const otherSubflowVariables = state.variables.filter(
        v => v.subflowId !== action.payload.subflowId,
      );
      newState = {
        variables: [...otherSubflowVariables, ...action.payload.variables],
      };
      break;
    }
    case "ADD_VARIABLE":
      newState = {
        variables: [...state.variables, action.payload],
      };
      break;
    case "UPDATE_VARIABLE":
      newState = {
        variables: state.variables.map(v =>
          v.name === action.payload.name
            ? {...v, ...action.payload.updates}
            : v,
        ),
      };
      break;
    case "REMOVE_VARIABLE":
      newState = {
        variables: state.variables.filter(v => v.name !== action.payload),
      };
      break;
    case "CLEAR_VARIABLES":
      newState = {variables: []};
      break;
    default:
      return state;
  }

  return newState;
};

export const VariablesProvider: React.FC<{children: ReactNode}> = ({
  children,
}) => {
  const [state, dispatch] = useReducer(variablesReducer, {variables: []});

  const getVariablesByType = (type: VariableType) =>
    state.variables.filter(v => v.type === type);

  const getAllVariables = () => {
    return state.variables;
  };

  const getSelectedSubflowVariables = (subflowId?: string) => {
    const currentSubflowId = subflowId || "main";
    return state.variables.filter(v => v.subflowId === currentSubflowId);
  };

  const addVariable = (variable: Variable) => {
    dispatch({type: "ADD_VARIABLE", payload: variable});
  };

  const updateVariable = (name: string, updates: Partial<Variable>) => {
    dispatch({type: "UPDATE_VARIABLE", payload: {name, updates}});
  };

  const removeVariable = (name: string) => {
    dispatch({type: "REMOVE_VARIABLE", payload: name});
  };

  const clearVariables = () => {
    dispatch({type: "CLEAR_VARIABLES"});
  };

  const syncVariablesWithNodes = (
    nodes: ProcessedNode[],
    subflowId: string,
    subflowName: string,
  ) => {
    const variablesSetting = nodes.reduce((acc: Variable[], node) => {
      const nodeSettings = node.data.settings;
      const action = nodeSettings.action as CustomAction;
      // Extract regular variables
      const variables = nodeSettings.variables || [];

      // Extract and convert llm_vars from action nodes
      const actionLlmVars = action?.llm_vars || [];
      const actionVariables = action?.variables || [];

      const convertedLlmVars = actionLlmVars
        .filter((llmVar: LLMVariable) => llmVar.name) // Only include variables with names
        .map((llmVar: LLMVariable) => ({
          id: llmVar.name!,
          name: llmVar.name!,
          type: llmVar.type as VariableType,
          value: llmVar.example || "",
          isValid: true,
          source: node.id,
          instructions: llmVar.description || "",
          required: true,
          subflowId,
          subflowName,
        }));

      const convertedActionVariables = actionVariables.map(variable => ({
        id: variable,
        name: action.name + "_" + variable.replace(/\./g, "_"),
        type: VariableType.STRING,
        subflowId,
        subflowName,
      }));

      const allVariables = [
        ...variables.map(v => ({...v, subflowId, subflowName})),
        ...convertedLlmVars,
        ...convertedActionVariables,
      ];

      // Deduplicate variables by name before adding to accumulator
      const uniqueVariables = allVariables.filter(
        variable => !acc.some(existing => existing.name === variable.name),
      );

      return [...acc, ...uniqueVariables];
    }, []);

    // Use SET_SUBFLOW_VARIABLES to preserve variables from other subflows
    dispatch({
      type: "SET_SUBFLOW_VARIABLES",
      payload: {subflowId, variables: variablesSetting as Variable[]},
    });
    return variablesSetting;
  };

  const value: VariablesContextType = {
    variables: state.variables,
    getVariablesByType,
    getAllVariables,
    getSelectedSubflowVariables,
    addVariable,
    updateVariable,
    removeVariable,
    clearVariables,
    syncVariablesWithNodes,
  };

  return (
    <VariablesContext.Provider value={value}>
      {children}
    </VariablesContext.Provider>
  );
};

export const useVariables = (): VariablesContextType => {
  const context = useContext(VariablesContext);
  if (context === undefined) {
    throw new Error("useVariables must be used within a VariablesProvider");
  }
  return context;
};
